/** In-memory access token per ADR-009 — never persisted to localStorage/sessionStorage. */

export interface AuthSession {
  accessToken: string;
  expiresAt: number | null;
}

/** Claims used by client auth flows (MOD-002 access token). */
export interface AccessTokenClaims {
  sub?: string;
  sid?: string;
  biz?: string;
  brn?: string;
  bac?: string;
  /** Merchant role codes from access token (`rol` claim). */
  rol?: string[];
  exp?: number;
  /** 10-03 impersonation — present on subject-surface tokens. */
  imp?: {
    grantId?: string;
    actorSub?: string;
    readOnly?: boolean;
    by?: string;
    ref?: string;
    ro?: boolean;
  };
}

let accessToken: string | null = null;
let expiresAt: number | null = null;
const listeners = new Set<() => void>();

function decodeJwtPayload(token: string): AccessTokenClaims | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    return JSON.parse(atob(segment.replace(/-/g, '+').replace(/_/g, '/'))) as AccessTokenClaims;
  } catch {
    return null;
  }
}

function decodeJwtExpiry(token: string): number | null {
  const payload = decodeJwtPayload(token);
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
}

export function parseAccessTokenClaims(token: string | null): AccessTokenClaims {
  if (!token) return {};
  return decodeJwtPayload(token) ?? {};
}

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getTokenExpiresAt(): number | null {
  return expiresAt;
}

export function setAccessToken(token: string): void {
  accessToken = token;
  expiresAt = decodeJwtExpiry(token);
  notify();
}

export function clearAccessToken(): void {
  accessToken = null;
  expiresAt = null;
  notify();
}

export function isAuthenticated(): boolean {
  return Boolean(accessToken);
}

export function subscribeAuthState(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAuthSession(): AuthSession | null {
  if (!accessToken) return null;
  return { accessToken, expiresAt };
}
